import * as React from 'react';
import React__default, { useRef, useEffect, useState, useCallback, useMemo } from 'react';
import styled, { css, keyframes } from 'styled-components';
import { scroller, createContextHookWithProvider, useOnKeyDown, FETCH_STATUSES, useMedia, getAbsoluteUrl, isNotNullish, parseUrl, getOrigin, isStringGuard, useUpdateEffect, notFalsy, isBrowser, assignRef, convertSrcSet, getImageTypeFromUrl, request, generateNumberArray, RequestError } from '@tager/web-core';
import Head from 'next/head';
import { useRouter } from 'next/router';
import NextLink from 'next/link';
import { StatusCodes, getReasonPhrase } from 'http-status-codes';

var __defProp$7 = Object.defineProperty;
var __getOwnPropSymbols$7 = Object.getOwnPropertySymbols;
var __hasOwnProp$7 = Object.prototype.hasOwnProperty;
var __propIsEnum$7 = Object.prototype.propertyIsEnumerable;
var __defNormalProp$7 = (obj, key, value) => key in obj ? __defProp$7(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __spreadValues$7 = (a, b) => {
  for (var prop in b || (b = {}))
    if (__hasOwnProp$7.call(b, prop))
      __defNormalProp$7(a, prop, b[prop]);
  if (__getOwnPropSymbols$7)
    for (var prop of __getOwnPropSymbols$7(b)) {
      if (__propIsEnum$7.call(b, prop))
        __defNormalProp$7(a, prop, b[prop]);
    }
  return a;
};
var __objRest$5 = (source, exclude) => {
  var target = {};
  for (var prop in source)
    if (__hasOwnProp$7.call(source, prop) && exclude.indexOf(prop) < 0)
      target[prop] = source[prop];
  if (source != null && __getOwnPropSymbols$7)
    for (var prop of __getOwnPropSymbols$7(source)) {
      if (exclude.indexOf(prop) < 0 && __propIsEnum$7.call(source, prop))
        target[prop] = source[prop];
    }
  return target;
};
function Overlay$1(_a) {
  var _b = _a, {
    onClick,
    hidden,
    scrollLockDisabled
  } = _b, rest = __objRest$5(_b, [
    "onClick",
    "hidden",
    "scrollLockDisabled"
  ]);
  const ref = useRef(null);
  useEffect(() => {
    if (scrollLockDisabled)
      return;
    if (!hidden) {
      scroller.lock(ref.current);
    }
    return () => scroller.unlockAll();
  }, [hidden, scrollLockDisabled]);
  function handleClick(event) {
    if (event.target === event.currentTarget) {
      onClick();
    }
  }
  let lastEvents = [];
  return /* @__PURE__ */ React__default.createElement(Container$4, __spreadValues$7({
    ref,
    onClick: (e) => {
      if (lastEvents.length >= 2 && lastEvents[lastEvents.length - 2] === "onMouseDown" && lastEvents[lastEvents.length - 1] === "onMouseUp") {
        handleClick(e);
      } else if (lastEvents.length >= 3 && lastEvents[lastEvents.length - 3] === "onMouseDown" && lastEvents[lastEvents.length - 1] === "onMouseUp") {
        handleClick(e);
      } else {
        lastEvents = [];
      }
    },
    onMouseDown: () => {
      lastEvents.push("onMouseDown");
    },
    onMouseUp: () => {
      lastEvents.push("onMouseUp");
    },
    onMouseMove: () => {
      lastEvents.push("onMouseMove");
    },
    hidden
  }, rest));
}
const Container$4 = styled.div`
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  padding: 10px;
  position: fixed;
  z-index: 10000;
  background: rgba(0, 0, 0, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;

  &[hidden] {
    display: none;
  }
`;

const [useCtx$1, CtxProvider$1] = createContextHookWithProvider("ModalContext");
function useModal() {
  const context = useCtx$1();
  return context;
}
const ModalContextProvider = CtxProvider$1;

function ModalProvider(props) {
  var _a, _b, _c, _d, _e, _f, _g, _h, _i, _j, _k, _l, _m, _n, _o, _p, _q;
  const [modal, setModal] = useState(null);
  const [isInnerContentVisible, setInnerContentVisible] = useState(false);
  const withAnimation = (_c = (_b = (_a = modal == null ? void 0 : modal.options) == null ? void 0 : _a.withAnimation) != null ? _b : props.withAnimation) != null ? _c : true;
  const shouldCloseOnEscape = (_f = (_e = (_d = modal == null ? void 0 : modal.options) == null ? void 0 : _d.shouldCloseOnEscape) != null ? _e : props.shouldCloseOnEscape) != null ? _f : true;
  const shouldCloseOnClickOutside = (_i = (_h = (_g = modal == null ? void 0 : modal.options) == null ? void 0 : _g.shouldCloseOnClickOutside) != null ? _h : props.shouldCloseOnClickOutside) != null ? _i : true;
  const openModal = useCallback((...args) => setModal({ type: args[0], props: args[1], options: args[2] }), []);
  useEffect(function showInnerContentIfModalOpen() {
    if (modal) {
      setInnerContentVisible(true);
    }
  }, [modal]);
  const closeModal = useCallback(function hideInnerContent() {
    setInnerContentVisible(false);
    if (!withAnimation) {
      setModal(null);
    }
  }, [withAnimation]);
  function handleTransitionEnd() {
    if (!isInnerContentVisible) {
      setModal(null);
    }
  }
  useOnKeyDown(["Escape", "Esc"], () => {
    if (modal && shouldCloseOnEscape) {
      closeModal();
    }
  });
  function handleOverlayClick() {
    if (shouldCloseOnClickOutside) {
      closeModal();
    }
  }
  const ModalOverlay = (_n = (_m = (_k = (_j = modal == null ? void 0 : modal.options) == null ? void 0 : _j.components) == null ? void 0 : _k.Overlay) != null ? _m : (_l = props.components) == null ? void 0 : _l.Overlay) != null ? _n : Overlay$1;
  const scrollLockDisabled = (_q = (_p = (_o = modal == null ? void 0 : modal.options) == null ? void 0 : _o.scrollLockDisabled) != null ? _p : props.scrollLockDisabled) != null ? _q : false;
  const isOpen = Boolean(modal);
  return /* @__PURE__ */ React__default.createElement(ModalContextProvider, {
    value: openModal
  }, /* @__PURE__ */ React__default.createElement(ModalOverlay, {
    "data-testid": "modal-overlay",
    "data-modal-overlay": true,
    onClick: handleOverlayClick,
    hidden: !isOpen,
    scrollLockDisabled
  }, /* @__PURE__ */ React__default.createElement(ModalInner, {
    isOpen: isInnerContentVisible,
    withAnimation,
    onTransitionEnd: handleTransitionEnd
  }, modal ? React__default.createElement(modal.type, {
    closeModal,
    innerProps: modal.props
  }) : null)), props.children);
}
const ModalInner = styled.div`
  transition: ${(props) => props.withAnimation ? "transform 0.35s ease-in-out, opacity 0.35s ease-in-out" : "none"};

  ${(props) => props.isOpen ? css`
          transform: scale(1);
          opacity: 1;
        ` : css`
          transform: scale(0);
          opacity: 0;
        `}
`;

const [
  useCtx,
  CtxProvider
] = createContextHookWithProvider("GalleryContext");
const useGallery = useCtx;
const GalleryContextProvider = CtxProvider;

function _extends$3() { _extends$3 = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$3.apply(this, arguments); }

function _objectWithoutProperties$3(source, excluded) { if (source == null) return {}; var target = _objectWithoutPropertiesLoose$3(source, excluded); var key, i; if (Object.getOwnPropertySymbols) { var sourceSymbolKeys = Object.getOwnPropertySymbols(source); for (i = 0; i < sourceSymbolKeys.length; i++) { key = sourceSymbolKeys[i]; if (excluded.indexOf(key) >= 0) continue; if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue; target[key] = source[key]; } } return target; }

function _objectWithoutPropertiesLoose$3(source, excluded) { if (source == null) return {}; var target = {}; var sourceKeys = Object.keys(source); var key, i; for (i = 0; i < sourceKeys.length; i++) { key = sourceKeys[i]; if (excluded.indexOf(key) >= 0) continue; target[key] = source[key]; } return target; }

var _ref2$3 = /*#__PURE__*/React.createElement("path", {
  d: "M0 0h24v24H0z",
  fill: "none"
});

var _ref3$3 = /*#__PURE__*/React.createElement("path", {
  d: "M15.41 7.41L14 6l-6 6 6 6 1.41-1.41L10.83 12z"
});

function SvgChevronLeft(_ref, svgRef) {
  var title = _ref.title,
      titleId = _ref.titleId,
      props = _objectWithoutProperties$3(_ref, ["title", "titleId"]);

  return /*#__PURE__*/React.createElement("svg", _extends$3({
    xmlns: "http://www.w3.org/2000/svg",
    height: 24,
    viewBox: "0 0 24 24",
    width: 24,
    ref: svgRef,
    "aria-labelledby": titleId
  }, props), title ? /*#__PURE__*/React.createElement("title", {
    id: titleId
  }, title) : null, _ref2$3, _ref3$3);
}

var ForwardRef$3 = /*#__PURE__*/React.forwardRef(SvgChevronLeft);

function _extends$2() { _extends$2 = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$2.apply(this, arguments); }

function _objectWithoutProperties$2(source, excluded) { if (source == null) return {}; var target = _objectWithoutPropertiesLoose$2(source, excluded); var key, i; if (Object.getOwnPropertySymbols) { var sourceSymbolKeys = Object.getOwnPropertySymbols(source); for (i = 0; i < sourceSymbolKeys.length; i++) { key = sourceSymbolKeys[i]; if (excluded.indexOf(key) >= 0) continue; if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue; target[key] = source[key]; } } return target; }

function _objectWithoutPropertiesLoose$2(source, excluded) { if (source == null) return {}; var target = {}; var sourceKeys = Object.keys(source); var key, i; for (i = 0; i < sourceKeys.length; i++) { key = sourceKeys[i]; if (excluded.indexOf(key) >= 0) continue; target[key] = source[key]; } return target; }

var _ref2$2 = /*#__PURE__*/React.createElement("path", {
  d: "M0 0h24v24H0z",
  fill: "none"
});

var _ref3$2 = /*#__PURE__*/React.createElement("path", {
  d: "M10 6L8.59 7.41 13.17 12l-4.58 4.59L10 18l6-6z"
});

function SvgChevronRight(_ref, svgRef) {
  var title = _ref.title,
      titleId = _ref.titleId,
      props = _objectWithoutProperties$2(_ref, ["title", "titleId"]);

  return /*#__PURE__*/React.createElement("svg", _extends$2({
    xmlns: "http://www.w3.org/2000/svg",
    height: 24,
    viewBox: "0 0 24 24",
    width: 24,
    ref: svgRef,
    "aria-labelledby": titleId
  }, props), title ? /*#__PURE__*/React.createElement("title", {
    id: titleId
  }, title) : null, _ref2$2, _ref3$2);
}

var ForwardRef$2 = /*#__PURE__*/React.forwardRef(SvgChevronRight);

function _extends$1() { _extends$1 = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$1.apply(this, arguments); }

function _objectWithoutProperties$1(source, excluded) { if (source == null) return {}; var target = _objectWithoutPropertiesLoose$1(source, excluded); var key, i; if (Object.getOwnPropertySymbols) { var sourceSymbolKeys = Object.getOwnPropertySymbols(source); for (i = 0; i < sourceSymbolKeys.length; i++) { key = sourceSymbolKeys[i]; if (excluded.indexOf(key) >= 0) continue; if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue; target[key] = source[key]; } } return target; }

function _objectWithoutPropertiesLoose$1(source, excluded) { if (source == null) return {}; var target = {}; var sourceKeys = Object.keys(source); var key, i; for (i = 0; i < sourceKeys.length; i++) { key = sourceKeys[i]; if (excluded.indexOf(key) >= 0) continue; target[key] = source[key]; } return target; }

var _ref2$1 = /*#__PURE__*/React.createElement("path", {
  d: "M0 0h24v24H0z",
  fill: "none"
});

var _ref3$1 = /*#__PURE__*/React.createElement("path", {
  d: "M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"
});

function SvgClose(_ref, svgRef) {
  var title = _ref.title,
      titleId = _ref.titleId,
      props = _objectWithoutProperties$1(_ref, ["title", "titleId"]);

  return /*#__PURE__*/React.createElement("svg", _extends$1({
    xmlns: "http://www.w3.org/2000/svg",
    height: 24,
    viewBox: "0 0 24 24",
    width: 24,
    ref: svgRef,
    "aria-labelledby": titleId
  }, props), title ? /*#__PURE__*/React.createElement("title", {
    id: titleId
  }, title) : null, _ref2$1, _ref3$1);
}

var ForwardRef$1 = /*#__PURE__*/React.forwardRef(SvgClose);

function getDisplayedPageNumbers({
  pagesCount,
  currentPageNumber,
  maxButtonCount
}) {
  const pageNumbers = [];
  const halfCount = Math.round(maxButtonCount / 2);
  for (let i = 1; i <= pagesCount; i++) {
    if (pageNumbers.length < maxButtonCount) {
      pageNumbers.push(i);
    } else {
      const currentPageIndex = pageNumbers.indexOf(currentPageNumber);
      if (currentPageIndex + 1 > halfCount || currentPageIndex === -1) {
        pageNumbers.shift();
        pageNumbers.push(i);
      }
    }
  }
  return pageNumbers;
}

function createMediaQuery({ min, max }) {
  if (!min && !max && process.env.NODE_ENV === "development") {
    throw new Error(`You didn't specify "min" or "max" width for @media mixin`);
  }
  return min && max ? `(min-width: ${min}px) and (max-width: ${max - 0.02}px)` : min ? `(min-width: ${min}px)` : max ? `(max-width: ${max - 0.02}px)` : "";
}
function createMediaMixin(params) {
  const mediaQuery = createMediaQuery(params);
  return (styles) => css`
    @media ${mediaQuery} {
      ${styles};
    }
  `;
}
const visuallyHidden = css`
  border: 0;
  clip: rect(0 0 0 0);
  height: 1px;
  width: 1px;
  margin: -1px;
  padding: 0;
  overflow: hidden;
  position: absolute;
`;
const placeholderAnimation = keyframes`
0% {
  background-position: -1200px 0;
}
100% {
  background-position: 1200px 0;
}
`;
const placeholderAnimationCss = css`
  animation: ${placeholderAnimation} 2s linear;
  animation-iteration-count: infinite;
  background-image: linear-gradient(
    to right,
    rgba(0, 0, 0, 0.08) 0,
    rgba(0, 0, 0, 0.15) 15%,
    rgba(0, 0, 0, 0.08) 30%
  );
  background-size: 1200px 100%;
`;

function Spinner(props) {
  var _a, _b;
  const size = (_a = props.size) != null ? _a : 100;
  const thickness = (_b = props.thickness) != null ? _b : 4;
  return /* @__PURE__ */ React__default.createElement(Container$3, {
    visible: Boolean(props.show)
  }, /* @__PURE__ */ React__default.createElement(Inner$3, {
    size
  }, /* @__PURE__ */ React__default.createElement(Circle, {
    thickness,
    color: props.color
  })));
}
const Container$3 = styled.div`
  opacity: ${(props) => props.visible ? 1 : 0};
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  pointer-events: none;
  transition: opacity 0.3s;
`;
const Inner$3 = styled.div`
  display: inline-block;
  position: relative;
  width: ${(props) => props.size}px;
  height: ${(props) => props.size}px;
`;
const Circle = styled.div`
  position: absolute;

  border: ${(props) => props.thickness}px solid ${(props) => {
  var _a;
  return (_a = props.color) != null ? _a : "white";
}};
  opacity: 1;
  border-radius: 50%;
  animation-name: ${(props) => keyframes`
    from {
        top: 50%;
        left: 50%;
        width: ${props.thickness * 2}px;
        height: ${props.thickness * 2}px;
        opacity: 1;
    }
    
    to {
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        opacity: 0;
    }
  `};
  animation-duration: 1s;
  animation-timing-function: cubic-bezier(0, 0.2, 0.8, 1);
  animation-iteration-count: infinite;
`;

var __defProp$6 = Object.defineProperty;
var __defProps$5 = Object.defineProperties;
var __getOwnPropDescs$5 = Object.getOwnPropertyDescriptors;
var __getOwnPropSymbols$6 = Object.getOwnPropertySymbols;
var __hasOwnProp$6 = Object.prototype.hasOwnProperty;
var __propIsEnum$6 = Object.prototype.propertyIsEnumerable;
var __defNormalProp$6 = (obj, key, value) => key in obj ? __defProp$6(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __spreadValues$6 = (a, b) => {
  for (var prop in b || (b = {}))
    if (__hasOwnProp$6.call(b, prop))
      __defNormalProp$6(a, prop, b[prop]);
  if (__getOwnPropSymbols$6)
    for (var prop of __getOwnPropSymbols$6(b)) {
      if (__propIsEnum$6.call(b, prop))
        __defNormalProp$6(a, prop, b[prop]);
    }
  return a;
};
var __spreadProps$5 = (a, b) => __defProps$5(a, __getOwnPropDescs$5(b));
var __objRest$4 = (source, exclude) => {
  var target = {};
  for (var prop in source)
    if (__hasOwnProp$6.call(source, prop) && exclude.indexOf(prop) < 0)
      target[prop] = source[prop];
  if (source != null && __getOwnPropSymbols$6)
    for (var prop of __getOwnPropSymbols$6(source)) {
      if (exclude.indexOf(prop) < 0 && __propIsEnum$6.call(source, prop))
        target[prop] = source[prop];
    }
  return target;
};
function LoadableImage(_a) {
  var _b = _a, {
    spinnerProps,
    spinnerContainerStyle
  } = _b, imageProps = __objRest$4(_b, [
    "spinnerProps",
    "spinnerContainerStyle"
  ]);
  const [status, setStatus] = useState(FETCH_STATUSES.LOADING);
  useEffect(() => {
    if (!imageProps.src)
      return;
    const image = new Image();
    image.addEventListener("load", () => {
      setStatus(FETCH_STATUSES.SUCCESS);
    });
    image.addEventListener("error", () => {
      setStatus(FETCH_STATUSES.FAILURE);
    });
    image.src = imageProps.src;
  }, [imageProps.src]);
  if (status === FETCH_STATUSES.LOADING) {
    return /* @__PURE__ */ React__default.createElement("div", {
      style: spinnerContainerStyle
    }, /* @__PURE__ */ React__default.createElement(Spinner, __spreadProps$5(__spreadValues$6({}, spinnerProps), {
      show: true
    })));
  }
  if (status === FETCH_STATUSES.SUCCESS) {
    return /* @__PURE__ */ React__default.createElement("img", __spreadProps$5(__spreadValues$6({}, imageProps), {
      alt: imageProps.alt
    }));
  }
  return /* @__PURE__ */ React__default.createElement("div", {
    style: spinnerContainerStyle
  }, /* @__PURE__ */ React__default.createElement("img", __spreadProps$5(__spreadValues$6({}, imageProps), {
    alt: imageProps.alt || "Loading error"
  })));
}

function Gallery({ options, onClose }) {
  var _a, _b;
  const { imageList, initialIndex } = options;
  const isMobile = useMedia(createMediaQuery({ max: 767.98 }));
  const [currentIndex, setCurrentIndex] = useState(initialIndex || 0);
  function prev() {
    setCurrentIndex((prev2) => prev2 !== 0 ? prev2 - 1 : prev2);
  }
  const next = useCallback(() => setCurrentIndex((prev2) => prev2 < imageList.length - 1 ? prev2 + 1 : prev2), [imageList.length]);
  useOnKeyDown(["ArrowLeft", "Left"], prev);
  useOnKeyDown(["ArrowRight", "Right"], next);
  useOnKeyDown(["Escape", "Esc"], onClose);
  const displayedImageIndexList = useMemo(() => getDisplayedPageNumbers({
    pagesCount: imageList.length,
    currentPageNumber: currentIndex + 1,
    maxButtonCount: isMobile ? 3 : 5
  }).map((number) => number - 1), [currentIndex, imageList.length, isMobile]);
  return /* @__PURE__ */ React__default.createElement(Overlay$1, {
    onClick: onClose
  }, /* @__PURE__ */ React__default.createElement(Container$2, null, /* @__PURE__ */ React__default.createElement(ImageContainer, null, /* @__PURE__ */ React__default.createElement(LoadableImage, {
    src: (_a = imageList[currentIndex]) == null ? void 0 : _a.url,
    spinnerContainerStyle: { width: "50vw", height: "50vh" },
    alt: ""
  })), /* @__PURE__ */ React__default.createElement(ImageFooter, null, /* @__PURE__ */ React__default.createElement(Caption, null, (_b = imageList[currentIndex]) == null ? void 0 : _b.caption), /* @__PURE__ */ React__default.createElement(PageNumber, null, currentIndex + 1, " of ", imageList.length))), /* @__PURE__ */ React__default.createElement(SmallGallery, null, imageList.map((image, index) => /* @__PURE__ */ React__default.createElement(SmallImageContainer, {
    key: image.url,
    onClick: () => setCurrentIndex(index),
    isActive: index === currentIndex,
    hidden: !displayedImageIndexList.includes(index)
  }, /* @__PURE__ */ React__default.createElement(LoadableImage, {
    src: image.url,
    alt: "",
    spinnerContainerStyle: { width: 50, height: 50 },
    spinnerProps: { size: 35, thickness: 2 }
  }))), /* @__PURE__ */ React__default.createElement(SmallArrowButton, {
    direction: "left",
    title: "Previous",
    onClick: prev,
    disabled: currentIndex === 0
  }, /* @__PURE__ */ React__default.createElement(ForwardRef$3, null)), /* @__PURE__ */ React__default.createElement(SmallArrowButton, {
    direction: "right",
    title: "Next",
    onClick: next,
    disabled: currentIndex === imageList.length - 1
  }, /* @__PURE__ */ React__default.createElement(ForwardRef$2, null))), /* @__PURE__ */ React__default.createElement(CloseButton, {
    type: "button",
    title: "Close",
    onClick: onClose
  }, /* @__PURE__ */ React__default.createElement(ForwardRef$1, null)), /* @__PURE__ */ React__default.createElement(ArrowButton, {
    direction: "left",
    title: "Previous",
    onClick: prev,
    disabled: currentIndex === 0
  }, /* @__PURE__ */ React__default.createElement(ForwardRef$3, null)), /* @__PURE__ */ React__default.createElement(ArrowButton, {
    direction: "right",
    title: "Next",
    onClick: next,
    disabled: currentIndex === imageList.length - 1
  }, /* @__PURE__ */ React__default.createElement(ForwardRef$2, null)));
}
const SmallGallery = styled.div`
  position: absolute;
  bottom: 0;
  left: 50%;
  transform: translate(-50%);
  display: flex;
`;
const Container$2 = styled.div`
  position: relative;
  user-select: none;
  max-width: calc(100vw - 192px);

  @media (max-width: 1023.98px) {
    max-width: calc(100vw - 160px);
  }
  @media (max-width: 767.98px) {
    max-width: calc(100vw - 100px);
  }
`;
const ImageContainer = styled.div`
  color: white;
  font-size: 1.5rem;
  img {
    height: 100%;
    width: 100%;
    object-fit: contain;
    max-height: calc(100vh - 290px);
    max-width: 1024px;
  }
`;
const SmallImageContainer = styled.div`
  position: relative;
  margin: 2px;
  cursor: pointer;
  border-radius: 2px;
  overflow: hidden;
  transition: transform 0.2s ease-in-out;
  font-size: 10px;
  color: white;

  ${(props) => props.isActive ? css`
          transform: translateY(-7px);
        ` : null}
  img {
    z-index: -1;
    height: 50px;
    width: 50px;
    object-fit: cover;
  }
`;
const ImageFooter = styled.div`
  position: absolute;
  width: 100%;
  display: flex;
  justify-content: space-between;
`;
const PageNumber = styled.span`
  flex: 0 0 auto;
  display: block;
  padding: 0.5rem;
  color: white;
  opacity: 0.7;
  font-size: 0.9em;

  @media (max-width: 767.98px) {
    font-size: 0.7em;
  }
`;
const Caption = styled.span`
  flex: 1 1 auto;
  display: block;
  padding: 0.5rem;
  color: white;
  opacity: 0.9;

  @media (max-width: 767.98px) {
    font-size: 0.8em;
  }
`;
const NavButton = styled.button`
  transition: background-color 0.1s;

  svg {
    display: block;
    height: 96px;
    width: 96px;
    fill: white;
    transition: opacity 0.1s;
    opacity: 0.9;

    @media (max-width: 1023.98px) {
      width: 80px;
      height: 80px;
    }

    @media (max-width: 767.98px) {
      width: 50px;
      height: 50px;
    }
  }

  &:disabled {
    opacity: 0.3;
  }

  &:not(:disabled):hover {
    background-color: rgba(255, 255, 255, 0.1);

    svg {
      opacity: 1;
    }
  }
`;
const ArrowButton = styled(NavButton)`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);

  ${(props) => props.direction === "left" ? css`
          left: 0;
        ` : css`
          right: 0;
        `}
`;
const SmallArrowButton = styled(NavButton)`
  position: absolute;

  ${(props) => props.direction === "left" ? css`
          left: 0;
          transform: translateX(-100%);
        ` : css`
          right: 0;
          transform: translateX(100%);
        `}

  svg {
    height: 50px;
    width: 50px;
  }
`;
const CloseButton = styled(NavButton)`
  position: absolute;
  top: 0;
  right: 0;
  padding: 1.3rem;

  svg {
    width: 50px;
    height: 50px;
  }

  @media (max-width: 767.98px) {
    padding: 0.7rem;

    svg {
      width: 35px;
      height: 35px;
    }
  }
`;

function GalleryProvider(props) {
  const [state, setState] = useState({ isOpen: false, options: null });
  const openGallery = useCallback((options) => setState({ isOpen: true, options }), []);
  const closeGallery = useCallback(() => setState({ isOpen: false, options: null }), []);
  function renderChildren() {
    if (typeof props.children === "function") {
      return props.children(openGallery);
    }
    return props.children;
  }
  return /* @__PURE__ */ React__default.createElement(GalleryContextProvider, {
    value: openGallery
  }, state.isOpen ? /* @__PURE__ */ React__default.createElement(Gallery, {
    onClose: closeGallery,
    options: state.options
  }) : null, renderChildren());
}

function getCurrentPath(basePath, asPath) {
  return basePath && basePath !== "/" ? basePath + asPath : asPath;
}
function getCanonicalUrl(currentPath, canonicalPath) {
  const absoluteUrl = getAbsoluteUrl(canonicalPath != null ? canonicalPath : currentPath);
  const parsedUrl = parseUrl(absoluteUrl);
  return parsedUrl ? parsedUrl.origin + parsedUrl.pathname + parsedUrl.search : absoluteUrl;
}
function getLdJsonData(currentPath, title, description, image, datePublished, dateModified, organizationName, logoSrc) {
  const currentUrl = getAbsoluteUrl(currentPath);
  const result = {
    "@context": "http://schema.org",
    "@type": "WebPage"
  };
  if (title) {
    result.name = title;
  }
  if (description) {
    result.description = description;
  }
  if (image) {
    result.image = [getAbsoluteUrl(image)];
  }
  if (datePublished) {
    result.datePublished = datePublished;
  }
  if (dateModified) {
    result.dateModified = dateModified;
  }
  if (organizationName) {
    result.publisher = {
      "@type": "Organization",
      name: organizationName
    };
    if (process.env.NEXT_PUBLIC_ORIGIN) {
      result.publisher.url = process.env.NEXT_PUBLIC_ORIGIN;
    }
    if (logoSrc) {
      result.publisher.logo = {
        "@type": "ImageObject",
        url: [getAbsoluteUrl(logoSrc)]
      };
    }
  }
  if (currentUrl) {
    result.mainEntityOfPage = {
      "@type": "WebPage",
      "@id": getAbsoluteUrl(currentUrl)
    };
  }
  return result;
}
function getMetaList({
  title,
  description,
  keywords,
  openGraphImage,
  currentPath
}) {
  const currentPageUrl = getAbsoluteUrl(currentPath);
  return [
    description ? {
      name: "description",
      content: description
    } : null,
    keywords ? {
      name: "keywords",
      content: keywords
    } : null,
    currentPageUrl ? {
      name: "og:url",
      content: currentPageUrl
    } : null,
    title ? {
      property: "og:title",
      content: title
    } : null,
    description ? {
      property: "og:description",
      content: description
    } : null,
    openGraphImage ? {
      property: "og:image",
      content: getAbsoluteUrl(openGraphImage)
    } : null,
    {
      property: "og:type",
      content: "website"
    },
    {
      name: "twitter:card",
      content: "summary"
    },
    title ? {
      name: "twitter:title",
      content: title
    } : null,
    description ? {
      name: "twitter:description",
      content: description
    } : null,
    openGraphImage ? {
      name: "twitter:image",
      content: getAbsoluteUrl(openGraphImage)
    } : null
  ].filter(isNotNullish);
}

var __defProp$5 = Object.defineProperty;
var __defProps$4 = Object.defineProperties;
var __getOwnPropDescs$4 = Object.getOwnPropertyDescriptors;
var __getOwnPropSymbols$5 = Object.getOwnPropertySymbols;
var __hasOwnProp$5 = Object.prototype.hasOwnProperty;
var __propIsEnum$5 = Object.prototype.propertyIsEnumerable;
var __defNormalProp$5 = (obj, key, value) => key in obj ? __defProp$5(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __spreadValues$5 = (a, b) => {
  for (var prop in b || (b = {}))
    if (__hasOwnProp$5.call(b, prop))
      __defNormalProp$5(a, prop, b[prop]);
  if (__getOwnPropSymbols$5)
    for (var prop of __getOwnPropSymbols$5(b)) {
      if (__propIsEnum$5.call(b, prop))
        __defNormalProp$5(a, prop, b[prop]);
    }
  return a;
};
var __spreadProps$4 = (a, b) => __defProps$4(a, __getOwnPropDescs$4(b));
function Page({
  children,
  title,
  description,
  keywords,
  openGraphImage,
  hiddenFromSeoIndexation,
  canonicalUrl,
  datePublished,
  dateModified
}) {
  const router = useRouter();
  const currentPath = getCurrentPath(router.basePath, router.asPath);
  const metaList = getMetaList({
    title,
    description,
    keywords,
    openGraphImage,
    currentPath
  });
  const canonicalUrlPrepared = getCanonicalUrl(currentPath, canonicalUrl);
  const homePageUrl = getOrigin();
  const jsonLdObject = getLdJsonData(currentPath, title, description, openGraphImage, datePublished, dateModified);
  return /* @__PURE__ */ React__default.createElement(React__default.Fragment, null, /* @__PURE__ */ React__default.createElement(Head, null, /* @__PURE__ */ React__default.createElement("title", null, title != null ? title : ""), homePageUrl ? /* @__PURE__ */ React__default.createElement("link", {
    href: homePageUrl,
    rel: "home"
  }) : null, canonicalUrlPrepared ? /* @__PURE__ */ React__default.createElement("link", {
    href: canonicalUrlPrepared,
    rel: "canonical"
  }) : null, hiddenFromSeoIndexation ? /* @__PURE__ */ React__default.createElement("meta", {
    name: "robots",
    content: "noindex, nofollow"
  }) : null, metaList.map((metaProps, index) => /* @__PURE__ */ React__default.createElement("meta", __spreadProps$4(__spreadValues$5({}, metaProps), {
    key: index
  }))), jsonLdObject ? /* @__PURE__ */ React__default.createElement("script", {
    type: "application/ld+json",
    dangerouslySetInnerHTML: { __html: JSON.stringify(jsonLdObject) }
  }) : null), children);
}

function isPreloaderEnabled() {
  const isEnabledEnv = process.env.NEXT_PUBLIC_SPLASHSCREEN_ENABLED;
  return isStringGuard(isEnabledEnv) && (isEnabledEnv.toLowerCase() === "true" || isEnabledEnv === "1");
}

const rotateCss = keyframes`
  100% {
    transform: rotate(360deg);
  }
`;
const bounceCss = keyframes`
  0%,
  100% {
    transform: scale(0);
  }

  50% {
    transform: scale(1);
  }
`;
const fadeOutCss = keyframes`
  0% {
    opacity: 1;
  }

  100% {
    opacity: 0;
  }
`;
const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  bottom: 0;
  right: 0;
  background: #fff;
  opacity: 1;
  will-change: opacity;
  z-index: 1000;

  &.fade-out {
    animation: ${fadeOutCss} 1s ease-in-out forwards;
  }
`;
const Inner$2 = styled.div`
  position: relative;
  top: 50%;
  left: 50%;
  width: 70px;
  height: 70px;
  margin-top: -35px;
  margin-left: -35px;
  text-align: center;
  animation: ${rotateCss} 2s infinite linear;
`;
const Item = styled.div`
  position: absolute;
  display: inline-block;
  top: 0;
  background-color: #f7f7f7;
  border-radius: 100%;
  width: 35px;
  height: 35px;
  animation: ${bounceCss} 2s infinite ease-in-out;

  &:last-child {
    top: auto;
    bottom: 0;
    animation-delay: -1s;
  }
`;

function Preloader({ hidden: hiddenProp, className, debug }) {
  const isControlled = hiddenProp !== void 0;
  function isInitiallyVisible() {
    const isVisible = isBrowser() ? isPreloaderEnabled() && !window.isPreloaderHidden : isPreloaderEnabled();
    return isControlled ? !hiddenProp : isVisible;
  }
  const [status, setStatus] = useState(isInitiallyVisible() ? "VISIBLE" : "HIDDEN");
  useEffect(() => {
    if (!debug)
      return;
    console.log(`%c [DEBUG Preloader]: isControlled - ${isControlled}`, "color: green");
    console.log(`%c [DEBUG Preloader]: isInitiallyVisible - ${isInitiallyVisible()}`, "color: green");
  }, [debug]);
  useEffect(() => {
    if (!debug)
      return;
    console.log(`%c [DEBUG Preloader]: status - ${status}`, "color: green");
  }, [debug, status]);
  function handleAnimationEnd() {
    setStatus("HIDDEN");
  }
  useUpdateEffect(() => {
    if (isControlled) {
      setStatus(hiddenProp ? "FADING_OUT" : "VISIBLE");
    }
  }, [hiddenProp, isControlled]);
  useEffect(() => {
    if (isControlled || status !== "VISIBLE")
      return;
    function hidePreloader() {
      setStatus("FADING_OUT");
      window.isPreloaderHidden = true;
    }
    if (document.readyState !== "loading") {
      hidePreloader();
      return;
    }
    document.addEventListener("DOMContentLoaded", hidePreloader);
    return () => {
      document.removeEventListener("DOMContentLoaded", hidePreloader);
    };
  }, [isControlled]);
  if (status === "HIDDEN") {
    return null;
  }
  const containerClassName = [
    status === "FADING_OUT" ? "fade-out" : void 0,
    className
  ].filter(notFalsy).join(" ");
  return /* @__PURE__ */ React__default.createElement(Overlay, {
    className: containerClassName,
    onAnimationEnd: handleAnimationEnd,
    "data-preloader-overlay": true
  }, /* @__PURE__ */ React__default.createElement(Inner$2, {
    "data-preloader": true
  }, /* @__PURE__ */ React__default.createElement(Item, {
    "data-preloader-item": true
  }), /* @__PURE__ */ React__default.createElement(Item, {
    "data-preloader-item": true
  })));
}

var __defProp$4 = Object.defineProperty;
var __getOwnPropSymbols$4 = Object.getOwnPropertySymbols;
var __hasOwnProp$4 = Object.prototype.hasOwnProperty;
var __propIsEnum$4 = Object.prototype.propertyIsEnumerable;
var __defNormalProp$4 = (obj, key, value) => key in obj ? __defProp$4(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __spreadValues$4 = (a, b) => {
  for (var prop in b || (b = {}))
    if (__hasOwnProp$4.call(b, prop))
      __defNormalProp$4(a, prop, b[prop]);
  if (__getOwnPropSymbols$4)
    for (var prop of __getOwnPropSymbols$4(b)) {
      if (__propIsEnum$4.call(b, prop))
        __defNormalProp$4(a, prop, b[prop]);
    }
  return a;
};
var __objRest$3 = (source, exclude) => {
  var target = {};
  for (var prop in source)
    if (__hasOwnProp$4.call(source, prop) && exclude.indexOf(prop) < 0)
      target[prop] = source[prop];
  if (source != null && __getOwnPropSymbols$4)
    for (var prop of __getOwnPropSymbols$4(source)) {
      if (exclude.indexOf(prop) < 0 && __propIsEnum$4.call(source, prop))
        target[prop] = source[prop];
    }
  return target;
};
const IMAGE_PLACEHOLDER = "data:image/gif;base64,R0lGODlhAQABAAAAACH5BAEKAAEALAAAAAABAAEAAAICTAEAOw==";
const Image$1 = React__default.forwardRef(function ImageWithRef(_a, ref) {
  var _b = _a, { className, src, srcSet, isLazy } = _b, rest = __objRest$3(_b, ["className", "src", "srcSet", "isLazy"]);
  const imgClassName = [className, isLazy ? "lazyload" : null].filter(Boolean).join(" ");
  return /* @__PURE__ */ React__default.createElement("img", __spreadValues$4({
    className: imgClassName,
    ref,
    src: isLazy ? IMAGE_PLACEHOLDER : src,
    srcSet: isLazy ? void 0 : srcSet,
    "data-src": src,
    "data-srcset": srcSet
  }, rest));
});

var __defProp$3 = Object.defineProperty;
var __defProps$3 = Object.defineProperties;
var __getOwnPropDescs$3 = Object.getOwnPropertyDescriptors;
var __getOwnPropSymbols$3 = Object.getOwnPropertySymbols;
var __hasOwnProp$3 = Object.prototype.hasOwnProperty;
var __propIsEnum$3 = Object.prototype.propertyIsEnumerable;
var __defNormalProp$3 = (obj, key, value) => key in obj ? __defProp$3(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __spreadValues$3 = (a, b) => {
  for (var prop in b || (b = {}))
    if (__hasOwnProp$3.call(b, prop))
      __defNormalProp$3(a, prop, b[prop]);
  if (__getOwnPropSymbols$3)
    for (var prop of __getOwnPropSymbols$3(b)) {
      if (__propIsEnum$3.call(b, prop))
        __defNormalProp$3(a, prop, b[prop]);
    }
  return a;
};
var __spreadProps$3 = (a, b) => __defProps$3(a, __getOwnPropDescs$3(b));
var __objRest$2 = (source, exclude) => {
  var target = {};
  for (var prop in source)
    if (__hasOwnProp$3.call(source, prop) && exclude.indexOf(prop) < 0)
      target[prop] = source[prop];
  if (source != null && __getOwnPropSymbols$3)
    for (var prop of __getOwnPropSymbols$3(source)) {
      if (exclude.indexOf(prop) < 0 && __propIsEnum$3.call(source, prop))
        target[prop] = source[prop];
    }
  return target;
};
function Source(_a) {
  var _b = _a, { srcList, isLazy, type } = _b, rest = __objRest$2(_b, ["srcList", "isLazy", "type"]);
  var _a2, _b2;
  if (srcList.length === 0)
    return null;
  return /* @__PURE__ */ React__default.createElement("source", __spreadProps$3(__spreadValues$3({}, rest), {
    srcSet: isLazy ? void 0 : convertSrcSet(srcList),
    "data-srcset": isLazy ? convertSrcSet(srcList) : void 0,
    type: (_b2 = type != null ? type : getImageTypeFromUrl((_a2 = srcList[0]) != null ? _a2 : null)) != null ? _b2 : void 0
  }));
}
function getSrcList(src1x, src2x) {
  const srcList = [];
  if (src1x) {
    srcList.push(src1x);
    if (src2x) {
      srcList.push(src2x);
    }
  }
  return srcList;
}
function SourceGroup({ media, images, isLazy }) {
  if (!images || Object.values(images).length === 0)
    return null;
  return /* @__PURE__ */ React__default.createElement(React__default.Fragment, null, /* @__PURE__ */ React__default.createElement(Source, {
    isLazy,
    srcList: getSrcList(images.webp, images.webp2x),
    media
  }), /* @__PURE__ */ React__default.createElement(Source, {
    isLazy,
    srcList: getSrcList(images.src, images.src2x),
    media
  }));
}
function Picture({
  src,
  src2x,
  srcWebp,
  srcWebp2x,
  alt,
  className,
  imageStyle,
  loading,
  imageRef: outerImageRef,
  mediaQueryList,
  imageMap,
  onStatusChange
}) {
  const isLazy = loading === "lazy";
  const innerImageRef = useRef(null);
  const [status, setStatus] = useState(FETCH_STATUSES.IDLE);
  const statusChangeHandler = useRef(onStatusChange);
  useEffect(function trackImageLoadStatus() {
    if (!innerImageRef.current)
      return;
    if (isLazy && status === "SUCCESS") {
      innerImageRef.current.setAttribute("src", IMAGE_PLACEHOLDER);
      innerImageRef.current.classList.remove("lazyloaded");
      innerImageRef.current.classList.add("lazyload");
    }
    if (innerImageRef.current.src && innerImageRef.current.complete && IMAGE_PLACEHOLDER !== innerImageRef.current.src) {
      setStatus(FETCH_STATUSES.SUCCESS);
      return;
    }
    if (IMAGE_PLACEHOLDER === innerImageRef.current.src) {
      const mutationObserver = new MutationObserver(function mutationCallback(_mutations, observer) {
        setStatus(FETCH_STATUSES.LOADING);
        observer.disconnect();
        if (!innerImageRef.current)
          return;
        if (innerImageRef.current.complete) {
          setStatus(FETCH_STATUSES.SUCCESS);
          return;
        }
        function handleLoad() {
          if (!innerImageRef.current)
            return;
          innerImageRef.current.removeEventListener("load", handleLoad);
          setStatus(FETCH_STATUSES.SUCCESS);
        }
        function handleError() {
          if (!innerImageRef.current)
            return;
          innerImageRef.current.removeEventListener("error", handleError);
          setStatus(FETCH_STATUSES.FAILURE);
        }
        innerImageRef.current.addEventListener("load", handleLoad);
        innerImageRef.current.addEventListener("error", handleError);
      });
      mutationObserver.observe(innerImageRef.current, {
        attributes: true,
        attributeFilter: ["src", "data-src"]
      });
      return;
    }
    if (innerImageRef.current.src && innerImageRef.current.src !== IMAGE_PLACEHOLDER) {
      setStatus(FETCH_STATUSES.LOADING);
      const handleLoad = () => {
        if (!innerImageRef.current)
          return;
        innerImageRef.current.removeEventListener("load", handleLoad);
        setStatus(FETCH_STATUSES.SUCCESS);
      };
      const handleError = () => {
        if (!innerImageRef.current)
          return;
        innerImageRef.current.removeEventListener("error", handleError);
        setStatus(FETCH_STATUSES.FAILURE);
      };
      innerImageRef.current.addEventListener("load", handleLoad);
      innerImageRef.current.addEventListener("error", handleError);
    }
  }, [src]);
  useEffect(function updateStatusChangeHandlerRef() {
    statusChangeHandler.current = onStatusChange;
  }, [onStatusChange]);
  useEffect(function callStatusChangeHandler() {
    if (statusChangeHandler.current) {
      statusChangeHandler.current(status);
    }
  }, [status]);
  return /* @__PURE__ */ React__default.createElement("picture", {
    className
  }, mediaQueryList.map((mediaQuery) => /* @__PURE__ */ React__default.createElement(SourceGroup, {
    key: mediaQuery.name,
    media: mediaQuery.value,
    images: imageMap[mediaQuery.name],
    isLazy
  })), src2x || srcWebp || srcWebp2x ? /* @__PURE__ */ React__default.createElement(SourceGroup, {
    images: {
      src,
      src2x,
      webp: srcWebp,
      webp2x: srcWebp2x
    },
    isLazy
  }) : null, /* @__PURE__ */ React__default.createElement(Image$1, {
    style: imageStyle,
    src: src != null ? src : void 0,
    srcSet: src2x ? `${src2x} 2x` : void 0,
    isLazy,
    alt: alt != null ? alt : "",
    ref: (imageNode) => {
      assignRef(innerImageRef, imageNode);
      assignRef(outerImageRef, imageNode);
    },
    "data-image-status": status.toLowerCase()
  }));
}
function dedupeMediaQueryList(list) {
  const map = new Map();
  list.forEach((item) => map.set(item.name, item));
  return Array.from(map.values());
}
function createPlainPictureComponent(options) {
  const uniqueMediaQueryList = dedupeMediaQueryList(options.mediaQueryList);
  function PlainPicture(props) {
    var _a;
    const imageMap = uniqueMediaQueryList.reduce((map, mediaQuery) => __spreadProps$3(__spreadValues$3({}, map), {
      [mediaQuery.name]: props[mediaQuery.name]
    }), {});
    let src = props.src;
    if (!props.src) {
      for (const device in imageMap) {
        if (imageMap[device]) {
          src = ((_a = imageMap[device]) == null ? void 0 : _a.src) || "";
          break;
        }
      }
    }
    return /* @__PURE__ */ React__default.createElement(Picture, __spreadProps$3(__spreadValues$3({}, props), {
      src,
      mediaQueryList: uniqueMediaQueryList,
      imageMap
    }));
  }
  return PlainPicture;
}

var __defProp$2 = Object.defineProperty;
var __defProps$2 = Object.defineProperties;
var __getOwnPropDescs$2 = Object.getOwnPropertyDescriptors;
var __getOwnPropSymbols$2 = Object.getOwnPropertySymbols;
var __hasOwnProp$2 = Object.prototype.hasOwnProperty;
var __propIsEnum$2 = Object.prototype.propertyIsEnumerable;
var __defNormalProp$2 = (obj, key, value) => key in obj ? __defProp$2(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __spreadValues$2 = (a, b) => {
  for (var prop in b || (b = {}))
    if (__hasOwnProp$2.call(b, prop))
      __defNormalProp$2(a, prop, b[prop]);
  if (__getOwnPropSymbols$2)
    for (var prop of __getOwnPropSymbols$2(b)) {
      if (__propIsEnum$2.call(b, prop))
        __defNormalProp$2(a, prop, b[prop]);
    }
  return a;
};
var __spreadProps$2 = (a, b) => __defProps$2(a, __getOwnPropDescs$2(b));
var __objRest$1 = (source, exclude) => {
  var target = {};
  for (var prop in source)
    if (__hasOwnProp$2.call(source, prop) && exclude.indexOf(prop) < 0)
      target[prop] = source[prop];
  if (source != null && __getOwnPropSymbols$2)
    for (var prop of __getOwnPropSymbols$2(source)) {
      if (exclude.indexOf(prop) < 0 && __propIsEnum$2.call(source, prop))
        target[prop] = source[prop];
    }
  return target;
};
function createPictureComponent(options) {
  const PlainPicture = createPlainPictureComponent(options);
  function SmartPicture(_a) {
    var _b = _a, {
      className,
      width,
      height,
      useSpinner,
      usePlaceholder,
      placeholderColor,
      spinnerComponent: SpinnerComponent,
      onStatusChange
    } = _b, plainPictureProps = __objRest$1(_b, [
      "className",
      "width",
      "height",
      "useSpinner",
      "usePlaceholder",
      "placeholderColor",
      "spinnerComponent",
      "onStatusChange"
    ]);
    const [status, setStatus] = useState(FETCH_STATUSES.IDLE);
    const isLoading = status === FETCH_STATUSES.LOADING;
    function renderSpinner() {
      return SpinnerComponent ? /* @__PURE__ */ React__default.createElement(SpinnerComponent, null) : /* @__PURE__ */ React__default.createElement(Spinner, {
        show: true
      });
    }
    const containerStyle = useMemo(() => width !== void 0 && height !== void 0 ? { width, height } : void 0, [height, width]);
    const imageStyle = useMemo(() => isLoading && (useSpinner || usePlaceholder) ? { opacity: 0 } : void 0, [isLoading, usePlaceholder, useSpinner]);
    function handleStatusChange(status2) {
      setStatus(status2);
      if (onStatusChange) {
        onStatusChange(status2);
      }
    }
    return /* @__PURE__ */ React__default.createElement(PictureContainer, {
      className,
      style: containerStyle,
      backgroundColor: isLoading && usePlaceholder ? placeholderColor : void 0,
      "data-picture-loading": isLoading,
      "data-picture-status": status.toLowerCase()
    }, isLoading && useSpinner ? renderSpinner() : null, /* @__PURE__ */ React__default.createElement(PlainPicture, __spreadProps$2(__spreadValues$2({}, plainPictureProps), {
      onStatusChange: handleStatusChange,
      imageStyle
    })));
  }
  return SmartPicture;
}
const PictureContainer = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background-color 0.3s;
  background-color: ${(props) => {
  var _a;
  return (_a = props.backgroundColor) != null ? _a : "transparent";
}};

  img {
    transition: opacity 0.3s;
  }
`;

var __defProp$1 = Object.defineProperty;
var __defProps$1 = Object.defineProperties;
var __getOwnPropDescs$1 = Object.getOwnPropertyDescriptors;
var __getOwnPropSymbols$1 = Object.getOwnPropertySymbols;
var __hasOwnProp$1 = Object.prototype.hasOwnProperty;
var __propIsEnum$1 = Object.prototype.propertyIsEnumerable;
var __defNormalProp$1 = (obj, key, value) => key in obj ? __defProp$1(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __spreadValues$1 = (a, b) => {
  for (var prop in b || (b = {}))
    if (__hasOwnProp$1.call(b, prop))
      __defNormalProp$1(a, prop, b[prop]);
  if (__getOwnPropSymbols$1)
    for (var prop of __getOwnPropSymbols$1(b)) {
      if (__propIsEnum$1.call(b, prop))
        __defNormalProp$1(a, prop, b[prop]);
    }
  return a;
};
var __spreadProps$1 = (a, b) => __defProps$1(a, __getOwnPropDescs$1(b));
var __objRest = (source, exclude) => {
  var target = {};
  for (var prop in source)
    if (__hasOwnProp$1.call(source, prop) && exclude.indexOf(prop) < 0)
      target[prop] = source[prop];
  if (source != null && __getOwnPropSymbols$1)
    for (var prop of __getOwnPropSymbols$1(source)) {
      if (exclude.indexOf(prop) < 0 && __propIsEnum$1.call(source, prop))
        target[prop] = source[prop];
    }
  return target;
};
function isLinkActive(to, router) {
  if (typeof to === "string") {
    return to === router.pathname;
  } else {
    return (to == null ? void 0 : to.as) === router.asPath;
  }
}
function isRenderFunction(children) {
  return typeof children === "function";
}
function createLinkComponent(options = {}) {
  return React__default.forwardRef((_a, ref) => {
    var _b = _a, {
      to,
      replace,
      scroll,
      shallow,
      passHref = true,
      prefetch,
      className,
      activeClassName,
      isActive: isActiveProp,
      disabled,
      children,
      onClick
    } = _b, restLinkProps = __objRest(_b, [
      "to",
      "replace",
      "scroll",
      "shallow",
      "passHref",
      "prefetch",
      "className",
      "activeClassName",
      "isActive",
      "disabled",
      "children",
      "onClick"
    ]);
    const router = useRouter();
    const convertedTo = useMemo(() => options.converter ? options.converter(to) : to, [to]);
    const isActive = useMemo(() => {
      if (!router)
        return false;
      if (typeof isActiveProp === "function") {
        return isActiveProp();
      } else if (typeof isActiveProp === "boolean") {
        return isActiveProp;
      } else {
        return isLinkActive(convertedTo, router);
      }
    }, [isActiveProp, router, convertedTo]);
    const linkClassName = [isActive ? activeClassName : null, className].filter(Boolean).join(" ");
    function handleLinkClick(event) {
      const path = typeof convertedTo === "string" ? convertedTo : convertedTo == null ? void 0 : convertedTo.as;
      if (!router || router.asPath === path || disabled) {
        event.preventDefault();
      }
      onClick == null ? void 0 : onClick(event);
    }
    const linkProps = __spreadProps$1(__spreadValues$1({}, restLinkProps), {
      className: linkClassName,
      isActive,
      onClick: handleLinkClick,
      ref,
      disabled
    });
    function renderLink() {
      if (isRenderFunction(children)) {
        return children(linkProps);
      } else {
        return children;
      }
    }
    const route = isNotNullish(convertedTo) && typeof convertedTo === "object" ? convertedTo : { href: convertedTo != null ? convertedTo : "" };
    return /* @__PURE__ */ React__default.createElement(NextLink, __spreadValues$1(__spreadProps$1(__spreadValues$1({}, route), {
      replace,
      scroll,
      shallow,
      passHref: disabled ? false : passHref,
      prefetch
    }), linkProps), renderLink());
  });
}

const breakpoints = {
  mobileSmall: 320,
  mobileMedium: 375,
  mobileLarge: 414,
  tabletSmall: 768,
  tabletLarge: 1024,
  laptop: 1260,
  desktop: 1500
};
const media = {
  mobileSmall: createMediaMixin({
    max: breakpoints.mobileMedium
  }),
  mobileMedium: createMediaMixin({
    max: breakpoints.mobileLarge
  }),
  mobileMediumOnly: createMediaMixin({
    min: breakpoints.mobileSmall,
    max: breakpoints.mobileLarge
  }),
  mobileLarge: createMediaMixin({
    max: breakpoints.tabletSmall
  }),
  mobileLargeOnly: createMediaMixin({
    min: breakpoints.mobileLarge,
    max: breakpoints.tabletSmall
  }),
  mobile: createMediaMixin({
    max: breakpoints.tabletSmall
  }),
  noMobile: createMediaMixin({
    min: breakpoints.tabletSmall
  }),
  tabletSmall: createMediaMixin({
    max: breakpoints.tabletLarge
  }),
  tabletSmallOnly: createMediaMixin({
    min: breakpoints.tabletSmall,
    max: breakpoints.tabletLarge
  }),
  tabletLarge: createMediaMixin({
    max: breakpoints.laptop
  }),
  tabletLargeOnly: createMediaMixin({
    min: breakpoints.tabletLarge,
    max: breakpoints.laptop
  }),
  tablet: createMediaMixin({
    max: breakpoints.laptop
  }),
  tabletOnly: createMediaMixin({
    min: breakpoints.tabletSmall,
    max: breakpoints.laptop
  }),
  laptop: createMediaMixin({
    max: breakpoints.desktop
  }),
  laptopOnly: createMediaMixin({
    min: breakpoints.laptop,
    max: breakpoints.desktop
  }),
  desktopOnly: createMediaMixin({
    min: breakpoints.desktop
  })
};

var __async = (__this, __arguments, generator) => {
  return new Promise((resolve, reject) => {
    var fulfilled = (value) => {
      try {
        step(generator.next(value));
      } catch (e) {
        reject(e);
      }
    };
    var rejected = (value) => {
      try {
        step(generator.throw(value));
      } catch (e) {
        reject(e);
      }
    };
    var step = (x) => x.done ? resolve(x.value) : Promise.resolve(x.value).then(fulfilled, rejected);
    step((generator = generator.apply(__this, __arguments)).next());
  });
};
function getErrorDetails(id) {
  return __async(this, null, function* () {
    var _a;
    return (_a = yield getIssueById(id)) == null ? void 0 : _a.data;
  });
}
function getIssueById(id) {
  return request.get({ path: `/tager/sentry-issue/${id}` });
}
function getFailureMessage(code, message) {
  if (code === 404)
    return "Tager Backend Sentry module is not installed";
  else
    return message;
}

function _extends() { _extends = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }

function _objectWithoutProperties(source, excluded) { if (source == null) return {}; var target = _objectWithoutPropertiesLoose(source, excluded); var key, i; if (Object.getOwnPropertySymbols) { var sourceSymbolKeys = Object.getOwnPropertySymbols(source); for (i = 0; i < sourceSymbolKeys.length; i++) { key = sourceSymbolKeys[i]; if (excluded.indexOf(key) >= 0) continue; if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue; target[key] = source[key]; } } return target; }

function _objectWithoutPropertiesLoose(source, excluded) { if (source == null) return {}; var target = {}; var sourceKeys = Object.keys(source); var key, i; for (i = 0; i < sourceKeys.length; i++) { key = sourceKeys[i]; if (excluded.indexOf(key) >= 0) continue; target[key] = source[key]; } return target; }

var _ref2 = /*#__PURE__*/React.createElement("path", {
  d: "M309.974 506.617C306.99 506.617 303.047 505.125 298.039 502.141L26.6316 317.687C19.1724 312.785 13.7379 307.777 10.2214 302.768C6.70498 297.867 5 291.899 5 284.866V261.103C5 254.177 6.59839 248.423 9.79518 243.947C12.992 239.472 18.6396 234.57 26.525 229.029L298.039 44.5744C302.941 41.5907 306.99 40.0989 309.974 40.0989C314.449 40.0989 318.179 41.4842 321.162 44.1482C324.146 46.9187 325.638 50.7548 325.638 55.6566V139.839C325.638 147.298 323.826 153.159 320.097 157.314C316.367 161.577 311.252 165.626 304.859 169.569L146.405 272.932L304.859 377.04C311.785 382.049 317.006 386.311 320.523 390.041C324.039 393.77 325.744 399.098 325.744 406.024V490.846C325.744 495.321 324.252 499.051 321.269 502.035C318.179 505.125 314.449 506.617 309.974 506.617Z",
  fill: "black"
});

var _ref3 = /*#__PURE__*/React.createElement("path", {
  d: "M528.421 263.448C530.766 263.448 533.004 263.448 535.348 263.448C539.61 263.448 543.766 263.448 548.028 263.448C548.135 241.816 548.348 224.98 548.774 212.832C549.733 180.651 557.938 156.675 573.283 141.011C588.628 125.347 609.513 117.568 635.727 117.568C661.514 117.568 682.08 125.347 697.425 141.011C712.769 156.675 720.975 180.545 721.934 212.832C722.68 234.251 723.212 251.194 723.319 263.448C771.377 263.554 819.542 263.768 867.601 264.3C867.387 250.661 866.961 232.759 866.215 210.594C864.191 145.167 842.559 94.6576 801.107 59.1732C759.656 23.6889 704.564 6 635.62 6C566.144 6 510.733 23.6889 469.387 59.1732C427.936 94.6576 406.304 145.06 404.28 210.594C403.853 223.062 403.64 240.964 403.534 264.3C445.305 263.768 486.863 263.554 528.421 263.448Z",
  fill: "black"
});

var _ref4 = /*#__PURE__*/React.createElement("path", {
  d: "M723.108 303.41C722.895 312.467 722.576 322.91 722.149 334.738C721.19 367.026 712.985 390.895 697.641 406.56C682.296 422.224 661.73 430.003 635.943 430.003C609.622 430.003 588.843 422.224 573.499 406.56C558.154 390.895 549.949 367.026 548.99 334.738C548.67 326.427 548.457 315.984 548.351 303.516C495.284 303.41 444.988 303.09 403.962 302.664C404.069 318.009 404.282 330.156 404.602 339.214C407.053 405.707 428.684 456.003 469.283 490.209C509.989 524.415 565.507 541.571 635.943 541.571C706.378 541.571 761.789 524.415 802.176 490.209C842.562 456.003 864.087 405.707 866.538 339.214C867.07 325.361 867.39 313.107 867.603 302.451C828.922 302.877 778.2 303.303 723.108 303.41Z",
  fill: "#DD6900"
});

var _ref5 = /*#__PURE__*/React.createElement("path", {
  d: "M939.311 410.61C980.017 362.019 1016.67 318.116 1049.39 278.902C1068.78 255.14 1084.34 236.279 1096.27 222.319C1108.21 208.467 1119.18 195.253 1129.41 182.892C1139.54 170.531 1148.38 159.982 1155.84 151.244C1163.3 142.613 1167.77 137.285 1169.27 135.26C1170.76 133.236 1171.08 131.531 1170.33 130.039C1169.59 128.547 1168.2 127.801 1166.28 127.801H954.229C948.795 127.801 944.319 126.096 940.803 122.58C937.286 119.063 935.581 114.694 935.581 109.153V77.9311C935.581 69.5128 937.286 61.4143 940.803 53.742C944.319 46.0697 948.901 39.2499 954.549 33.2826C960.197 27.3152 967.016 22.5201 975.008 18.7905C982.894 15.0609 991.419 13.2494 1000.26 13.2494H1331.34C1337.31 13.2494 1341.89 15.0609 1345.09 18.7905C1348.29 22.5201 1349.89 27.1021 1349.89 32.5367V106.915C1349.89 116.293 1346.9 124.817 1340.93 132.17C1300.76 181.72 1264.85 226.688 1233.1 266.861C1213.7 290.624 1198.36 309.592 1186.96 323.764C1175.55 337.936 1165 351.043 1155.31 363.191C1145.61 375.339 1137.3 385.675 1130.37 394.093C1123.45 402.512 1119.5 407.52 1118.44 409.012C1117.48 410.504 1117.48 411.995 1118.44 413.487C1119.4 414.979 1120.46 415.725 1121.42 415.725H1340.08C1345.52 415.725 1349.99 417.43 1353.51 420.946C1357.02 424.463 1358.73 428.832 1358.73 434.373V513.227C1358.73 518.662 1357.02 523.137 1353.51 526.654C1349.99 530.17 1345.62 531.875 1340.08 531.875H947.303C941.868 531.875 937.286 530.063 933.557 526.334C929.827 522.604 928.016 518.022 928.016 512.588V436.717C928.016 429.791 928.975 425.102 930.999 422.545C931.958 420.52 933.131 418.709 934.303 417.004C935.688 415.192 937.286 413.168 939.311 410.61Z",
  fill: "black"
});

var _ref6 = /*#__PURE__*/React.createElement("path", {
  d: "M1436.2 515.572V80.383C1436.2 61.5219 1442.6 46.0708 1455.6 33.923C1468.49 21.7752 1483.62 15.7013 1500.99 15.7013H1560.45C1566.42 15.7013 1570.89 17.4062 1573.88 20.9227C1577.29 24.4392 1579.1 28.9147 1579.1 34.3492V515.679C1579.1 521.114 1577.39 525.589 1573.88 529.105C1570.89 532.622 1566.42 534.327 1560.45 534.327H1454.85C1449.42 534.327 1444.94 532.622 1441.42 529.105C1438.01 525.482 1436.2 521.007 1436.2 515.572Z",
  fill: "black"
});

var _ref7 = /*#__PURE__*/React.createElement("path", {
  d: "M1689.92 506.622C1685.44 506.622 1681.71 505.13 1678.73 502.146C1675.75 499.162 1674.26 495.433 1674.26 490.957V406.136C1674.26 399.209 1675.96 393.881 1679.48 390.152C1682.89 386.422 1688.11 382.053 1695.14 377.152L1853.6 273.043L1695.14 169.68C1688.64 165.737 1683.63 161.581 1679.9 157.426C1676.17 153.27 1674.36 147.409 1674.36 139.95V55.8742C1674.36 50.9725 1675.85 47.0298 1678.84 44.3658C1681.82 41.5952 1685.55 40.3165 1690.03 40.3165C1693.01 40.3165 1696.95 41.8083 1701.96 44.792L1973.48 229.247C1981.36 234.681 1987.01 239.69 1990.2 244.165C1993.4 248.641 1995 254.288 1995 261.321V285.084C1995 292.01 1993.3 297.978 1989.78 302.986C1986.26 307.994 1980.83 312.896 1973.37 317.904L1701.75 502.146C1696.85 505.13 1692.9 506.622 1689.92 506.622Z",
  fill: "black"
});

var _ref8 = /*#__PURE__*/React.createElement("path", {
  d: "M558.042 638.324V656.546H543.55C535.877 678.71 528.312 700.768 520.959 722.826C513.5 744.884 506.041 766.941 498.582 789.106H472.688C468.532 775.892 464.482 762.892 460.327 749.892C456.171 736.892 452.122 723.891 447.966 710.678L421.219 789.106H395.325L353.661 656.546H338.529V638.324H372.948L408.219 757.138C412.801 742.966 417.383 728.9 422.072 714.834C426.76 700.768 431.449 686.702 436.138 672.53L425.695 638.324H450.31L485.581 757.138L524.476 638.324H558.042V638.324Z",
  fill: "#DD6900"
});

var _ref9 = /*#__PURE__*/React.createElement("path", {
  d: "M612.064 791.557C605.777 791.557 599.49 790.704 593.097 789.106C586.597 787.508 580.736 784.524 575.408 780.368C570.08 776.212 565.711 770.565 562.301 763.532C558.891 756.499 557.186 747.654 557.186 736.892V719.842C557.186 709.08 558.785 700.448 562.088 693.948C565.391 687.448 569.547 682.44 574.769 678.817C579.99 675.3 585.638 672.956 592.031 671.89C598.318 670.825 604.499 670.292 610.36 670.292C616.114 670.292 621.974 670.931 627.729 672.21C633.483 673.489 638.598 675.833 643.18 679.35C647.762 682.866 651.385 687.661 654.156 693.842C656.926 700.022 658.311 708.014 658.311 717.924C658.311 721.334 658.205 724.744 657.992 728.367C657.779 731.883 657.352 734.867 656.82 737.318H579.884C579.884 744.031 580.949 749.572 582.974 754.154C584.998 758.736 587.662 762.253 590.753 765.023C593.843 767.687 597.253 769.605 600.876 770.778C604.499 771.95 607.802 772.483 610.999 772.483C619.63 772.483 626.77 772.163 632.311 771.417C637.958 770.671 643.073 769.819 647.762 768.86L653.942 784.417C648.721 786.868 642.434 788.68 635.188 789.852C627.622 790.917 619.95 791.557 612.064 791.557ZM609.081 687.768C605.564 687.768 602.048 688.087 598.638 688.727C595.228 689.366 592.138 690.858 589.474 693.202C586.81 695.547 584.572 698.956 582.867 703.432C581.162 707.908 580.203 713.875 580.097 721.441H634.122C634.868 720.482 635.294 719.416 635.401 718.244C635.508 717.072 635.614 715.58 635.614 713.555C635.508 708.867 634.655 704.924 633.057 701.62C631.458 698.317 629.54 695.76 627.089 693.628C624.638 691.604 621.868 690.112 618.778 689.153C615.688 688.301 612.491 687.768 609.081 687.768Z",
  fill: "#DD6900"
});

var _ref10 = /*#__PURE__*/React.createElement("path", {
  d: "M712.342 625.536V677.857C714.047 676.259 717.244 674.66 722.039 672.955C726.834 671.25 733.335 670.398 741.646 670.398C747.507 670.398 753.155 671.144 758.909 672.636C764.557 674.127 769.671 676.791 774.147 680.734C778.622 684.677 782.139 690.005 784.909 696.825C787.573 703.644 788.959 712.276 788.959 722.719V740.194C788.959 750.957 787.573 759.695 784.803 766.408C782.032 773.121 778.409 778.343 773.827 782.072C769.245 785.802 764.237 788.253 758.483 789.638C752.835 791.023 747.081 791.663 741.22 791.663C732.269 791.663 725.343 791.023 720.334 789.638C715.326 788.253 710.85 786.654 707.014 784.843C706.055 787.72 704.777 789.851 703.285 791.45C701.793 793.048 700.301 794.113 698.916 794.859L684.424 789.958C685.17 788.572 686.022 785.589 687.088 781.22C688.153 776.851 688.686 770.67 688.686 762.785V643.545H673.128V625.643H712.342V625.536ZM712.342 753.514C712.342 758.523 712.129 762.892 711.703 766.941C713.088 767.687 714.687 768.433 716.285 769.179C717.883 769.924 719.695 770.67 721.72 771.31C723.744 771.949 725.982 772.482 728.646 772.802C731.31 773.121 734.4 773.334 737.917 773.334C741.859 773.334 745.589 772.908 748.999 771.949C752.409 770.99 755.286 769.179 757.843 766.515C760.294 763.851 762.319 760.015 763.704 755.006C765.089 749.998 765.835 743.604 765.835 735.719V726.768C765.835 718.989 764.983 712.595 763.384 707.8C761.786 702.898 759.655 699.062 756.991 696.292C754.327 693.521 751.343 691.71 747.827 690.751C744.31 689.792 740.794 689.365 737.064 689.365C732.056 689.365 727.474 690.005 723.105 691.283C718.736 692.562 715.219 694.161 712.342 695.972V753.514V753.514Z",
  fill: "#DD6900"
});

var _ref11 = /*#__PURE__*/React.createElement("path", {
  d: "M870.048 656.546V638.324H978.846V667.628H960.198V656.546H909.369V703.325H958.28V721.973H909.369V770.884H962.116V759.802H980.764V789.106H874.95V770.884H884.754V656.546H870.048V656.546Z",
  fill: "#DD6900"
});

var _ref12 = /*#__PURE__*/React.createElement("path", {
  d: "M1118.01 672.318V690.646C1115.88 690.646 1113.64 690.859 1111.41 691.179C1109.17 691.499 1106.61 692.458 1103.95 694.056L1096.91 702.368C1092.76 707.163 1088.82 711.958 1084.87 716.753C1080.93 721.548 1076.99 726.237 1072.83 730.926L1106.82 771.099H1117.91V789.32H1091.8L1057.81 748.721L1024.03 789.32H998.347V771.099C1001.22 771.099 1003.67 770.779 1005.7 770.246C1007.72 769.713 1010.17 768.435 1012.84 766.41L1042.78 730.926L1009 690.753H998.347V672.425H1023.81C1029.68 679.244 1035.32 686.064 1040.86 692.884C1046.41 699.704 1052.05 706.63 1057.91 713.343L1092.12 672.318H1118.01V672.318Z",
  fill: "#DD6900"
});

var _ref13 = /*#__PURE__*/React.createElement("path", {
  d: "M1170.65 826.823C1166.81 828.102 1162.98 829.381 1158.93 830.553C1154.99 831.725 1151.04 833.004 1147.21 834.282V700.657C1147.21 696.394 1146.67 693.411 1145.5 691.706C1144.33 690.001 1142.62 689.148 1140.17 689.148C1137.19 689.148 1134.31 689.681 1131.65 690.64L1129.73 676.361C1132.71 675.296 1135.48 674.23 1137.94 673.377C1140.39 672.525 1143.05 672.099 1145.93 672.099C1149.87 672.099 1153.17 672.738 1155.84 674.017C1158.5 675.296 1160.74 677.427 1162.76 680.304C1166.71 677.64 1171.61 675.295 1177.36 673.377C1183.12 671.459 1190.58 670.394 1199.74 670.394C1205.6 670.394 1211.36 671.14 1217.11 672.525C1222.86 673.91 1227.98 676.681 1232.45 680.623C1236.93 684.566 1240.55 690.001 1243.32 696.821C1246.09 703.64 1247.48 712.591 1247.48 723.46V741.256C1247.48 751.699 1246.09 760.224 1243.32 766.724C1240.55 773.224 1236.93 778.339 1232.45 781.962C1227.98 785.691 1222.97 788.142 1217.32 789.527C1211.68 790.913 1206.03 791.552 1200.17 791.552C1191.85 791.552 1185.35 791.019 1180.88 789.847C1176.3 788.675 1172.99 787.396 1170.65 785.798V826.823V826.823ZM1170.65 767.47C1173.53 768.748 1176.62 769.92 1180.24 770.879C1183.76 771.839 1188.87 772.371 1195.58 772.371C1199.42 772.371 1203.04 771.945 1206.56 771.093C1210.08 770.24 1213.06 768.535 1215.72 765.978C1218.39 763.42 1220.41 759.797 1222.01 755.002C1223.61 750.207 1224.36 743.92 1224.36 736.035V727.083C1224.36 719.198 1223.61 712.698 1222.12 707.796C1220.63 702.788 1218.6 698.952 1216.04 696.075C1213.49 693.198 1210.5 691.279 1207.2 690.214C1203.9 689.148 1200.27 688.615 1196.44 688.615C1191 688.615 1186.1 689.468 1181.63 691.066C1177.15 692.665 1173.53 694.476 1170.65 696.501V767.47V767.47Z",
  fill: "#DD6900"
});

var _ref14 = /*#__PURE__*/React.createElement("path", {
  d: "M1323.04 791.557C1316.76 791.557 1310.47 790.704 1304.07 789.106C1297.57 787.508 1291.71 784.524 1286.39 780.368C1281.06 776.212 1276.69 770.565 1273.28 763.532C1269.87 756.499 1268.16 747.654 1268.16 736.892V719.842C1268.16 709.08 1269.76 700.448 1273.07 693.948C1276.37 687.448 1280.53 682.44 1285.75 678.817C1290.97 675.3 1296.62 672.956 1303.01 671.89C1309.3 670.825 1315.48 670.292 1321.34 670.292C1327.2 670.292 1332.95 670.931 1338.71 672.21C1344.46 673.489 1349.58 675.833 1354.16 679.35C1358.74 682.866 1362.36 687.661 1365.13 693.842C1367.9 700.022 1369.29 708.014 1369.29 717.924C1369.29 721.334 1369.18 724.744 1368.97 728.367C1368.76 731.883 1368.33 734.867 1367.8 737.318H1290.86C1290.86 744.031 1291.93 749.572 1293.95 754.154C1295.98 758.736 1298.64 762.253 1301.73 765.023C1304.82 767.687 1308.23 769.605 1311.85 770.778C1315.48 771.95 1318.78 772.483 1321.98 772.483C1330.61 772.483 1337.75 772.163 1343.29 771.417C1348.94 770.671 1354.05 769.819 1358.74 768.86L1364.92 784.417C1359.7 786.868 1353.41 788.68 1346.17 789.852C1338.6 790.917 1331.03 791.557 1323.04 791.557ZM1320.06 687.768C1316.54 687.768 1313.03 688.087 1309.62 688.727C1306.21 689.366 1303.12 690.858 1300.45 693.202C1297.79 695.547 1295.55 698.956 1293.85 703.432C1292.14 707.908 1291.18 713.875 1291.07 721.441H1345.1C1345.85 720.482 1346.27 719.416 1346.38 718.244C1346.49 717.072 1346.59 715.58 1346.59 713.555C1346.49 708.867 1345.63 704.924 1344.03 701.62C1342.44 698.317 1340.52 695.76 1338.07 693.628C1335.62 691.604 1332.85 690.112 1329.76 689.153C1326.77 688.301 1323.47 687.768 1320.06 687.768Z",
  fill: "#DD6900"
});

var _ref15 = /*#__PURE__*/React.createElement("path", {
  d: "M1460.28 698.419L1443.87 699.698L1442.81 690.96C1440.14 690.96 1437.16 691.173 1433.86 691.493C1430.55 691.812 1427.57 692.558 1424.69 693.624C1421.82 694.689 1419.47 696.181 1417.55 698.099C1415.64 700.017 1414.68 702.575 1414.68 705.665V789.208H1391.13V694.902L1377.06 695.968L1376.42 679.345C1380.68 677.746 1385.27 676.468 1390.17 675.509C1395.07 674.55 1400.08 673.697 1405.41 672.951V684.033C1409.14 679.878 1413.4 676.681 1418.51 674.123C1423.52 671.673 1430.02 670.394 1437.91 670.394C1441.32 670.394 1444.83 670.607 1448.56 670.927C1452.29 671.246 1456.13 671.886 1460.28 672.738V698.419Z",
  fill: "#DD6900"
});

var _ref16 = /*#__PURE__*/React.createElement("path", {
  d: "M1512.61 635.978V671.675H1537.22V690.536H1512.4V761.505C1512.4 768.857 1514.85 772.587 1519.64 772.587C1521.77 772.587 1524.01 772.374 1526.46 771.948C1528.91 771.521 1531.9 770.989 1535.63 770.243L1537.97 787.079C1534.67 787.825 1530.94 788.571 1526.57 789.53C1522.2 790.489 1517.94 790.915 1513.46 790.915C1504.94 790.915 1498.76 787.612 1494.92 780.899C1490.98 774.292 1489.06 765.234 1489.06 753.939V690.643H1470.2L1471.9 671.782H1486.82C1488.1 668.904 1489.17 664.962 1490.02 659.847C1490.87 654.732 1491.3 647.486 1491.3 638.215L1512.61 635.978Z",
  fill: "#DD6900"
});

var _ref17 = /*#__PURE__*/React.createElement("path", {
  d: "M1628.86 703.961V691.813C1625.98 690.641 1622.89 689.789 1619.38 689.043C1615.86 688.297 1612.35 687.977 1608.62 687.977C1600.41 687.977 1594.02 689.149 1589.54 691.6C1584.96 694.051 1582.72 697.248 1582.72 701.404C1582.72 705.986 1584.43 709.396 1587.73 711.74C1591.03 714.084 1596.57 715.896 1604.25 717.174C1608.51 718.347 1613.52 719.306 1619.38 720.158C1625.24 721.011 1630.67 722.502 1636 724.634C1641.22 726.765 1645.7 730.068 1649.43 734.437C1653.16 738.806 1654.97 745.093 1654.97 753.298C1654.97 766.192 1650.81 775.782 1642.5 782.069C1634.19 788.356 1622.47 791.553 1607.34 791.553C1598.17 791.553 1589.54 791.02 1581.76 790.061C1573.88 789.102 1566.52 787.61 1559.6 785.799L1561.73 755.003H1579.84V767.151C1582.51 768.323 1585.92 769.389 1590.07 770.454C1594.23 771.52 1598.92 772.053 1604.35 772.053C1610.75 772.053 1615.76 771.307 1619.38 769.708C1623 768.11 1625.67 766.298 1627.48 764.274C1629.29 762.249 1630.35 760.118 1630.67 758.093C1630.99 756.069 1631.21 754.47 1631.21 753.511C1631.21 750.847 1630.46 748.503 1628.86 746.691C1627.26 744.88 1625.03 743.282 1622.15 742.109C1619.27 740.937 1615.76 739.872 1611.49 739.019C1607.34 738.167 1602.65 737.314 1597.32 736.462C1584.32 734.224 1574.84 730.494 1569.08 725.38C1563.22 720.265 1560.34 713.019 1560.34 703.535C1560.34 697.035 1561.52 691.6 1563.75 687.231C1565.99 682.862 1569.3 679.452 1573.45 676.682C1577.61 674.018 1582.72 672.1 1588.69 670.928C1594.66 669.755 1601.37 669.223 1608.83 669.223C1614.26 669.223 1619.91 669.649 1626.09 670.395C1632.17 671.141 1639.41 672.526 1647.72 674.551L1645.81 703.855H1628.86V703.961Z",
  fill: "#DD6900"
});

function SvgOzitagLogo(_ref, svgRef) {
  var title = _ref.title,
      titleId = _ref.titleId,
      props = _objectWithoutProperties(_ref, ["title", "titleId"]);

  return /*#__PURE__*/React.createElement("svg", _extends({
    viewBox: "0 0 2000 840",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg",
    ref: svgRef,
    "aria-labelledby": titleId
  }, props), title ? /*#__PURE__*/React.createElement("title", {
    id: titleId
  }, title) : null, _ref2, _ref3, _ref4, _ref5, _ref6, _ref7, _ref8, _ref9, _ref10, _ref11, _ref12, _ref13, _ref14, _ref15, _ref16, _ref17);
}

var ForwardRef = /*#__PURE__*/React.forwardRef(SvgOzitagLogo);

function ErrorCardFooter() {
  return /* @__PURE__ */ React__default.createElement(ErrorFooter, null, /* @__PURE__ */ React__default.createElement(FooterTager, {
    href: "https://ozitag.com/tager",
    target: "_blank"
  }, "Made by ", /* @__PURE__ */ React__default.createElement("span", null, "TAGER")), /* @__PURE__ */ React__default.createElement(FooterOZiTAG, {
    href: "https://ozitag.com/",
    target: "_blank"
  }, /* @__PURE__ */ React__default.createElement(ForwardRef, null)));
}
const ErrorFooter = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-shrink: 0;
  padding: 15px 20px;
  border-top: 1px solid rgb(230, 230, 230);

  ${media.mobile(css`
    padding: 15px;
  `)}
`;
const FooterTager = styled.a`
  display: flex;
  align-items: center;
  font-size: 15px;
  line-height: 22px;

  span {
    font-weight: 700;
    display: inline-block;
    margin-left: 6px;
    letter-spacing: 0.2em;
    font-size: 18px;
  }
`;
const FooterOZiTAG = styled.a`
  display: inline-block;
  height: 36px;

  svg {
    height: 36px;
  }
`;

var __defProp = Object.defineProperty;
var __defProps = Object.defineProperties;
var __getOwnPropDescs = Object.getOwnPropertyDescriptors;
var __getOwnPropSymbols = Object.getOwnPropertySymbols;
var __hasOwnProp = Object.prototype.hasOwnProperty;
var __propIsEnum = Object.prototype.propertyIsEnumerable;
var __defNormalProp = (obj, key, value) => key in obj ? __defProp(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __spreadValues = (a, b) => {
  for (var prop in b || (b = {}))
    if (__hasOwnProp.call(b, prop))
      __defNormalProp(a, prop, b[prop]);
  if (__getOwnPropSymbols)
    for (var prop of __getOwnPropSymbols(b)) {
      if (__propIsEnum.call(b, prop))
        __defNormalProp(a, prop, b[prop]);
    }
  return a;
};
var __spreadProps = (a, b) => __defProps(a, __getOwnPropDescs(b));
function ErrorDevelop({
  getErrorDetails: getErrorDetails$1,
  errorId,
  errorCode,
  errorName
}) {
  const [fetchingState, setFetchingState] = useState({
    isLoading: !!errorId,
    errorTitle: null,
    error: null
  });
  const { isLoading, error, errorTitle } = fetchingState;
  const errorDetailsRef = useRef(null);
  const errorDetailsCurrent = errorDetailsRef.current;
  const callStackLength = errorDetailsCurrent == null ? void 0 : errorDetailsCurrent.stacktrace.length;
  const [isCallStackCollapse, setCallStackCollapse] = useState(true);
  const [openedStackTraceCodeLines, setOpenedStackTraceCodeLines] = useState([]);
  const callStackAvailableLength = !isCallStackCollapse ? callStackLength : 3;
  function expandCallStack() {
    setCallStackCollapse(false);
  }
  function expandViewCode(index) {
    if (openedStackTraceCodeLines.indexOf(index) === -1) {
      setOpenedStackTraceCodeLines([...openedStackTraceCodeLines, index]);
    } else {
      setOpenedStackTraceCodeLines(openedStackTraceCodeLines.filter((item) => item !== index));
    }
  }
  const errorDetailsPromise = getErrorDetails$1 ? getErrorDetails$1 : getErrorDetails;
  useEffect(() => {
    if (!errorId)
      return;
    if (error || errorDetailsCurrent)
      return;
    errorDetailsPromise(errorId).then((response) => {
      errorDetailsRef.current = response;
      setFetchingState((prevFetchingState) => __spreadProps(__spreadValues({}, prevFetchingState), {
        isLoading: false
      }));
    }).catch((error2) => {
      var _a, _b;
      const code = (_a = error2.status) == null ? void 0 : _a.code;
      const message = (_b = error2.body) == null ? void 0 : _b.message;
      setFetchingState({
        error: getFailureMessage(code, message),
        errorTitle: code !== 404 ? "Tager Sentry Response:" : null,
        isLoading: false
      });
    });
  }, [error, errorDetailsCurrent, errorId]);
  let title, sentryUrl, file, stacktrace;
  if (errorDetailsCurrent) {
    title = errorDetailsCurrent.title;
    sentryUrl = errorDetailsCurrent.sentryUrl;
    file = errorDetailsCurrent.file;
    stacktrace = errorDetailsCurrent.stacktrace;
  }
  return /* @__PURE__ */ React__default.createElement(Container$1, null, /* @__PURE__ */ React__default.createElement(Inner$1, null, /* @__PURE__ */ React__default.createElement(Card, null, /* @__PURE__ */ React__default.createElement(CardScroll, null, /* @__PURE__ */ React__default.createElement(CardInner, null, /* @__PURE__ */ React__default.createElement(Block, null, /* @__PURE__ */ React__default.createElement(ErrorName, null, !isLoading ? `${errorCode !== 0 ? errorCode + " - " : ""}${errorName}` : `Loading error details...`), !isLoading && !errorDetailsCurrent && errorId ? /* @__PURE__ */ React__default.createElement(ErrorID, null, "Error ID: ", errorId) : null, isLoading || title || error ? /* @__PURE__ */ React__default.createElement(ErrorMessage, {
    isLoading
  }, errorTitle ? /* @__PURE__ */ React__default.createElement(ErrorMessageTitle, null, errorTitle) : null, title != null ? title : error) : null, sentryUrl ? /* @__PURE__ */ React__default.createElement(GetDetailsLink, {
    href: sentryUrl,
    target: "_blank"
  }, "View on Sentry") : null), isLoading || file ? /* @__PURE__ */ React__default.createElement(Block, null, /* @__PURE__ */ React__default.createElement(Title, {
    isLoading
  }, "Source"), /* @__PURE__ */ React__default.createElement(SourceCode, {
    isLoading
  }, /* @__PURE__ */ React__default.createElement(File, null, file))) : null, isLoading || stacktrace ? /* @__PURE__ */ React__default.createElement(Block, null, /* @__PURE__ */ React__default.createElement(Title, {
    isLoading
  }, "Call Stack"), stacktrace ? /* @__PURE__ */ React__default.createElement(CallStack, null, stacktrace.slice(0, callStackAvailableLength).map((item, index) => /* @__PURE__ */ React__default.createElement(CallStackItem, {
    key: index,
    isLoading
  }, /* @__PURE__ */ React__default.createElement(CallStackFunction, null, index + 1, ". ", item.function), /* @__PURE__ */ React__default.createElement(CallStackFile, null, item.file, " -", " ", /* @__PURE__ */ React__default.createElement(CallStackFileLine, null, "Line ", item.line, ", Col ", item.col)), /* @__PURE__ */ React__default.createElement("br", null), /* @__PURE__ */ React__default.createElement(CallStackButton, {
    onClick: () => expandViewCode(index)
  }, openedStackTraceCodeLines.indexOf(index) !== -1 ? "Hide code" : "View code"), openedStackTraceCodeLines.indexOf(index) !== -1 ? /* @__PURE__ */ React__default.createElement(CallStackCode, null, item.code.map((codeLine) => /* @__PURE__ */ React__default.createElement(CallStackCodeLine, {
    isActive: codeLine.line === item.line
  }, codeLine.line, codeLine.code))) : null)), isCallStackCollapse && stacktrace.length > 3 ? /* @__PURE__ */ React__default.createElement(CallStackButton, {
    onClick: expandCallStack
  }, "Show collapsed frames") : null) : /* @__PURE__ */ React__default.createElement(CallStack, null, generateNumberArray(3).map((num) => /* @__PURE__ */ React__default.createElement(CallStackItem, {
    key: num,
    isLoading
  })))) : null)), /* @__PURE__ */ React__default.createElement(ErrorCardFooter, null))));
}
const Container$1 = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 100vh;
  color: #262626;
  background-color: rgb(180, 180, 180);
`;
const Inner$1 = styled.div`
  width: 100%;
  max-width: 720px;
`;
const Card = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: calc(100% - 20px);
  max-height: calc(100vh - 20px);
  margin: 0 auto;
  border-top: 5px solid #ff5555;
  border-radius: 5px;
  font-size: 16px;
  line-height: 24px;
  background-color: #fff;
  box-shadow: 0 5px 25px 1px rgba(0, 0, 0, 0.25);
`;
const CardScroll = styled.div`
  overflow-y: auto;
`;
const CardInner = styled.div`
  padding: 35px 30px;

  ${media.mobile(css`
    padding: 35px 15px;
  `)}
`;
const loadingPlaceholderAnimation = keyframes`
  0%{
    background-position: 50% 0;
  }
  100%{
    background-position: -50% 0;
  }
`;
const loadingPlaceholderCss = css`
  position: relative;
  background: #f6f7f8;
  overflow: hidden;

  &:after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    display: block;
    width: 100%;
    height: 100%;
    background: #f6f7f8;
    background: linear-gradient(to right, #eee 2%, #ddd 18%, #eee 33%);
    background-size: 300%;
    animation-name: ${loadingPlaceholderAnimation};
    animation-duration: 1.5s;
    animation-fill-mode: forwards;
    animation-iteration-count: infinite;
    animation-timing-function: linear;
  }
`;
const preloader = css`
  ${loadingPlaceholderCss};
  min-height: 24px;
  border-radius: 5px;
`;
const ErrorName = styled.span`
  position: relative;
  display: block;
  font-size: 24px;
  font-weight: 700;
`;
const ErrorID = styled.span`
  position: relative;
  display: block;
  margin-top: 10px;
  word-break: break-all;
  color: #999999;
`;
const ErrorMessageTitle = styled.span`
  display: block;
  font-weight: 700;
`;
const ErrorMessage = styled.span`
  position: relative;
  display: block;
  margin-top: 10px;
  word-break: break-word;
  color: #ff5555;

  ${({ isLoading }) => isLoading && css`
      ${preloader};
      max-width: 500px;
    `}
`;
const GetDetailsLink = styled.a`
  display: inline-block;
  margin-top: 5px;
  text-decoration: underline;
  color: #5566ff;
`;
const Block = styled.div`
  &:not(:last-child) {
    margin-bottom: 30px;
  }
`;
const Title = styled.span`
  display: block;
  font-size: 24px;

  &:not(:last-child) {
    margin-bottom: 20px;
  }

  ${({ isLoading }) => isLoading && css`
      ${preloader};
      max-width: 350px;
    `}
`;
const SourceCode = styled.code`
  display: block;
  padding: 15px 25px;
  border-radius: 5px;
  background-color: #000;

  ${media.mobile(css`
    padding: 15px;
  `)}

  ${({ isLoading }) => isLoading && css`
      ${preloader};
      min-height: 78px;
    `}
`;
const File = styled.span`
  word-break: break-word;
  color: #b1b1b1;
`;
const CallStack = styled.ul`
  max-width: 620px;
`;
const CallStackItem = styled.li`
  font-size: 14px;
  line-height: 22px;

  &:not(:last-child) {
    margin-bottom: 10px;
  }

  ${({ isLoading }) => isLoading && css`
      ${preloader};
      min-height: 48px;
    `}
`;
const CallStackFile = styled.span`
  word-break: break-word;
`;
const CallStackFileLine = styled.span`
  font-weight: 700;
`;
const CallStackFunction = styled.span`
  display: block;
  background: #fff;
  font-size: 16px;
  margin-bottom: 5px;
  font-weight: 700;
`;
const CallStackCode = styled.code`
  display: block;
  padding: 5px 0 5px 15px;
  word-break: break-word;
  color: #999999;
  overflow: auto;
`;
const CallStackButton = styled.code`
  display: inline-block;
  cursor: pointer;

  &:not(:hover) {
    text-decoration: underline;
  }
`;
const CallStackCodeLine = styled.div`
  white-space: nowrap;
  background: ${({ isActive }) => isActive ? "#ececec" : "transparent"};
`;

function ErrorProd({ errorCode, errorName, errorId }) {
  return /* @__PURE__ */ React__default.createElement(Container, null, /* @__PURE__ */ React__default.createElement(Inner, null, errorCode !== 0 ? /* @__PURE__ */ React__default.createElement(Code, null, errorCode) : null, /* @__PURE__ */ React__default.createElement(Name, null, errorName), errorId ? /* @__PURE__ */ React__default.createElement(EventId, null, "Error ID: ", errorId) : null));
}
const Container = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 100vh;
  padding-bottom: 35px;
  text-align: center;
  color: #262626;
  background-color: rgb(245, 245, 245);
`;
const Inner = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  max-width: 960px;
`;
const Code = styled.span`
  max-width: 100%;
  font-size: 180px;
  font-weight: 900;
  line-height: 1;
  text-transform: uppercase;
  text-shadow: 0 0 1px #000;

  ${media.tabletLarge(css`
    font-size: 160px;
  `)}

  ${media.tabletSmall(css`
    font-size: 130px;
  `)}
  
  ${media.mobile(css`
    font-size: 100px;
  `)}
`;
const Name = styled.span`
  max-width: 100%;
  margin-top: 15px;
  font-size: 60px;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  color: #000;

  ${media.tabletLarge(css`
    font-size: 40px;
  `)}

  ${media.tabletSmall(css`
    font-size: 30px;
  `)}
  
  ${media.mobile(css`
    font-size: 20px;
  `)}
`;
const EventId = styled.span`
  max-width: 100%;
  margin-top: 50px;
  font-size: 15px;
  color: #aaaaaa;

  ${media.tabletSmall(css`
    margin-top: 35px;
    font-size: 13px;
  `)}

  ${media.mobile(css`
    margin-top: 20px;
    font-size: 12px;
  `)}
`;

function getErrorName(code) {
  const codeString = String(code);
  if (codeString === "404")
    return "Page Not Found";
  return "Server Error";
}
function Error$1({ getErrorDetails, errorCode, errorName, errorId }) {
  const isDevelopment = process.env.NEXT_PUBLIC_ENV !== "production";
  return isDevelopment ? /* @__PURE__ */ React__default.createElement(ErrorDevelop, {
    errorCode,
    errorName,
    errorId,
    getErrorDetails
  }) : /* @__PURE__ */ React__default.createElement(ErrorProd, {
    errorCode,
    errorName: getErrorName(errorCode),
    errorId
  });
}

function convertErrorToProps(error) {
  if (!error) {
    return {
      code: StatusCodes.INTERNAL_SERVER_ERROR
    };
  }
  if (error instanceof RequestError) {
    const errorObject = error.asObject();
    return {
      code: errorObject.statusCode,
      message: errorObject.errorMessage
    };
  }
  if ("statusCode" in error && "errorMessage" in error) {
    return {
      message: error.errorMessage,
      code: error.statusCode
    };
  }
  return { message: error.toString(), code: StatusCodes.INTERNAL_SERVER_ERROR };
}
function ErrorPage({
  error,
  code,
  message
}) {
  let errorName = message || "Unknown error";
  let errorCode = code !== void 0 ? code : StatusCodes.INTERNAL_SERVER_ERROR;
  if (error) {
    const { code: code2, message: message2 } = convertErrorToProps(error);
    errorCode = code2 !== void 0 ? code2 : StatusCodes.INTERNAL_SERVER_ERROR;
    errorName = message2 || "";
    if (errorName === "" && errorCode) {
      errorName = getReasonPhrase(errorCode);
    }
  }
  return /* @__PURE__ */ React__default.createElement(Page, {
    title: errorName
  }, /* @__PURE__ */ React__default.createElement(Error$1, {
    errorCode,
    errorName
  }));
}

export { Error$1 as ErrorModule, ErrorPage, GalleryProvider, Image$1 as Image, LoadableImage, Overlay$1 as ModalOverlay, ModalProvider, Page, Preloader, Spinner, createLinkComponent, createMediaMixin, createMediaQuery, createPictureComponent, createPlainPictureComponent, placeholderAnimation, placeholderAnimationCss, useGallery, useModal, visuallyHidden };
